'use client';

/**
 * "How did you spend your credits" breakdown for the credit usage overlay.
 *
 * Mantine components used: `Box`, `Stack`, `Text`, `Table` (with `Table.Thead`,
 * `Table.Tbody`, `Table.Tfoot`, `Table.Tr`, `Table.Th`, `Table.Td`).
 */

import { Box, Stack, Table, Text } from '@mantine/core';
import { tokens } from 'ui-lib/src/lib/css/tokens';

import type { CreditPlanKind, CreditSummary } from './derive-credit-summary-view';
import { formatBreakdownCredits, formatCreditCount } from './format';
import {
  breakdownFooterRow,
  breakdownShadedRow,
  breakdownTableFrame,
  breakdownTotalTable,
  hairlineColor,
} from './styles.css';

/**
 * The app theme rules every cell individually, on top of the row rule Mantine
 * already draws. That second rule is what survives under the last row and the
 * footer, so it is cleared here and the row keeps sole ownership of separators.
 */
const CELL_RULES_OFF = {
  th: { borderBottom: 'none' },
  td: { borderBottom: 'none' },
} as const;

/**
 * Body rows sit on 14px padding around a 20px line (Figma `2413:11836`); the
 * header and total rows are tighter, 11px around a 16px label line.
 */
const BODY_ROW_PADDING = 14;
const EDGE_ROW_PADDING = 11;

interface CreditUsageBreakdownTableProps {
  planKind: CreditPlanKind;
  summary: CreditSummary;
}

export interface BreakdownRow {
  label: string;
  /**
   * Milli-credits this row accounts for out of `consumedThisMonthMilli`, kept
   * unformatted so the rows can be summed against the month's total.
   */
  accountedMilli: number;
  value: string;
}

export function buildCaptureRows(planKind: CreditPlanKind, summary: CreditSummary): BreakdownRow[] {
  const { leadCaptureEnrichedMilli, leadCaptureNotEnrichedMilli } = summary.breakdown;

  // Pay as you go has no enriched/not-enriched split to show, so the two
  // capture buckets collapse into a single row.
  if (planKind === 'PAYG') {
    const capturedMilli = leadCaptureEnrichedMilli + leadCaptureNotEnrichedMilli;

    return [
      {
        label: 'Leads captured',
        accountedMilli: capturedMilli,
        value: formatBreakdownCredits(capturedMilli),
      },
    ];
  }

  // An unlimited package still shows its capture usage; the plan card is where
  // lead capture reads as unlimited.
  return [
    {
      label: 'Leads captured (enriched)',
      accountedMilli: leadCaptureEnrichedMilli,
      value: formatBreakdownCredits(leadCaptureEnrichedMilli),
    },
    {
      label: 'Leads captured (not enriched)',
      accountedMilli: leadCaptureNotEnrichedMilli,
      value: formatBreakdownCredits(leadCaptureNotEnrichedMilli),
    },
  ];
}

export function buildBulkEnrichmentRows(summary: CreditSummary): BreakdownRow[] {
  const { emailsMilli, phoneNumbersMilli, companyInfoMilli } = summary.breakdown.bulkEnrichment;

  // Every row takes an en dash. The design frame still draws a hyphen on the
  // Email row; that is the frame's typo, not a variant to copy.
  const rows: BreakdownRow[] = [
    {
      label: 'Bulk enrichment – Email',
      accountedMilli: emailsMilli,
      value: formatBreakdownCredits(emailsMilli),
    },
    {
      label: 'Bulk enrichment – Phone',
      accountedMilli: phoneNumbersMilli,
      value: formatBreakdownCredits(phoneNumbersMilli),
    },
  ];

  if (companyInfoMilli > 0) {
    rows.push({
      label: 'Bulk enrichment – Company',
      accountedMilli: companyInfoMilli,
      value: formatBreakdownCredits(companyInfoMilli),
    });
  }

  return rows;
}

/**
 * Every row the table renders, with the unattributed remainder appended.
 *
 * The footer totals `consumedThisMonthMilli`, which the detail rows cannot always
 * reach: a flat-rate `BULK_ENRICHMENT` pricing rule bills the month without a
 * per-fieldset split, so its credits reach no `bulkEnrichment` bucket. Those
 * credits land in "Other usage" instead of making the rows disagree with the
 * total. A policy-priced workspace always sums to the total, so the row never
 * appears for it.
 */
export function buildBreakdownRows(
  planKind: CreditPlanKind,
  summary: CreditSummary,
): BreakdownRow[] {
  const rows = [...buildCaptureRows(planKind, summary), ...buildBulkEnrichmentRows(summary)];
  const accountedMilli = rows.reduce((total, row) => total + row.accountedMilli, 0);
  const otherUsageMilli = summary.consumedThisMonthMilli - accountedMilli;

  if (otherUsageMilli > 0) {
    rows.push({
      label: 'Other usage',
      accountedMilli: otherUsageMilli,
      value: formatBreakdownCredits(otherUsageMilli),
    });
  }

  return rows;
}

export function CreditUsageBreakdownTable({ planKind, summary }: CreditUsageBreakdownTableProps) {
  const rows = buildBreakdownRows(planKind, summary);
  const tableProps = {
    horizontalSpacing: 'md',
    verticalSpacing: BODY_ROW_PADDING,
    styles: CELL_RULES_OFF,
    borderColor: hairlineColor,
  } as const;

  return (
    <Stack gap="md" h="100%">
      <Text fw={600} size="sm" lh="16px" c={tokens.color.foreground25.contentSecondary}>
        How did you spend your credits
      </Text>
      {/* The frame stretches to the plan card's height with the total pinned to its
          foot, so the total sits in its own table below the rows rather than a tfoot
          the browser would push the spare height into. */}
      <Box className={breakdownTableFrame}>
        <Table {...tableProps}>
          <Table.Thead className={breakdownShadedRow}>
            <Table.Tr>
              <Table.Th py={EDGE_ROW_PADDING} fz="xs" lh="16px">
                Details
              </Table.Th>
              <Table.Th py={EDGE_ROW_PADDING} fz="xs" lh="16px" ta="right">
                Credits
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.label}>
                <Table.Td>
                  <Text size="sm" lh="20px">
                    {row.label}
                  </Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm" fw={600} lh="16px">
                    {row.value}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Table {...tableProps} className={breakdownTotalTable}>
          <Table.Tbody className={breakdownShadedRow}>
            {/* The frame closes the table, so the total row carries no rule below it. */}
            <Table.Tr className={breakdownFooterRow}>
              <Table.Td py={EDGE_ROW_PADDING}>
                <Text size="sm" fw={600} lh="16px">
                  Total credits used
                </Text>
              </Table.Td>
              <Table.Td py={EDGE_ROW_PADDING} ta="right">
                <Text size="sm" fw={600} lh="16px">
                  {formatCreditCount(summary.consumedThisMonthMilli)}
                </Text>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Box>
    </Stack>
  );
}
